<template>
    <div>
        <v-container grid-list-xs>
            <v-sheet class="pa-5" elevation="3">
                <PageHeader>
                    <template #header>
                        <h4>{{ $t("down-payment.heading", { providerKey: providerKey }) }}</h4>
                    </template>
                    <template v-if="downPaymentRequired" #subheader>
                        {{
                            $t("down-payment.description-downpayment-required", {
                                downpaymentAmount: downpaymentAmountDollars,
                                providerKey: providerKey,
                            })
                        }}
                    </template>
                    <template v-else #subheader>
                        {{ $t("down-payment.description") }}
                    </template>
                </PageHeader>
                <v-row>
                    <v-col>
                        <Form :validations="validations" :messages="messages" @submit="submit">
                            <v-row class="mt-0">
                                <v-col class="py-0">
                                    <v-radio-group v-model="cardApplication.bankCardType" :column="false" class="mt-2">
                                        <v-radio label="Credit" value="CREDIT" class="pr-5" color="secondary"></v-radio>
                                        <v-radio label="Debit" value="DEBIT" class="pr-5" color="secondary"></v-radio>
                                        <v-radio label="HSA/FSA" value="HSA" class="pr-5" color="secondary"></v-radio>
                                    </v-radio-group>
                                </v-col>
                            </v-row>

                            <v-row v-min-children="1" dense>
                                <v-col v-min-children="1">
                                    <AppField
                                        :application="application"
                                        :field="fields['bankCardCardholderName']"
                                        label-code="field.bankCardCardholderName"
                                        :required="downPaymentRequired"
                                    />
                                </v-col>
                            </v-row>
                            <v-row v-min-children="1" dense>
                                <v-col v-min-children="1" cols="12" sm="9">
                                    <AppField
                                        label-code="field.bankCardCardNumber"
                                        :mask="bankCardCardNumberMask"
                                        :application="application"
                                        :field="fields['bankCardCardNumber']"
                                        :append-icon="showCreditCardNumber ? '$eye' : '$eye'"
                                        :validations="cardNumberValidations"
                                        :required="downPaymentRequired"
                                        @click:append="toggleMask"
                                        @focus="focus"
                                        @blur="blur"
                                        @input="handleBankCardChange"
                                    />
                                </v-col>
                                <v-col v-min-children="1" class="d-flex align-center items-center">
                                    <DownpaymentSupportedAssociations :provider-key="providerKey" />
                                </v-col>
                            </v-row>
                            <v-row v-min-children="1" dense>
                                <v-col v-min-children="1" cols="6" sm="4">
                                    <AppField
                                        :id="fields['bankCardExpirationMonth']"
                                        type="select"
                                        :application="application"
                                        :field="fields['bankCardExpirationMonth']"
                                        label-code="field.bankCardExpirationMonth"
                                        :required="downPaymentRequired"
                                        @input="handleBankCardChange"
                                    />
                                </v-col>
                                <v-col v-min-children="1" cols="6" sm="4">
                                    <AppField
                                        :id="fields['bankCardExpirationYear']"
                                        ref="bankCardExpirationYear"
                                        type="select"
                                        :application="application"
                                        :field="fields['bankCardExpirationYear']"
                                        label-code="field.bankCardExpirationYear"
                                        :required="downPaymentRequired"
                                    />
                                </v-col>
                                <v-col v-min-children="1" cols="12" sm="4">
                                    <AppField
                                        type="text"
                                        :mask="zipCodeMask"
                                        :application="application"
                                        :field="fields['bankCardZipCode']"
                                        :validations="zipCodeValidations"
                                        label-code="field.bankCardZipCode"
                                        :required="downPaymentRequired"
                                    />
                                </v-col>
                            </v-row>
                            <v-card-actions class="pb-12 px-0">
                                <v-row justify="space-between">
                                    <v-col>
                                        <BackButton
                                            v-if="backButtonDisabled"
                                            :to="backPath"
                                            class="mr-4"
                                            color="primary"
                                            rounded
                                        >
                                            {{ $t("action.back") }}
                                        </BackButton>
                                    </v-col>
                                    <v-col class="d-flex justify-end">
                                        <SubmitButton class="secondary submit-button" :submitting="submitting" rounded>
                                            {{ $t("action.continue") }}
                                        </SubmitButton>
                                    </v-col>
                                </v-row>
                            </v-card-actions>
                        </Form>
                    </v-col>
                </v-row>
                <HfdBankCardErrorModal
                    v-model="showBankCardErrorModal"
                    :error-retry-message="errorRetryMessage"
                    @close-modal="closeBankCardErrorModal"
                >
                </HfdBankCardErrorModal>
                <ResponsiveImage :image-url="footerLogo" width="100px" class="footer-logo-center" />
            </v-sheet>
        </v-container>
    </div>
</template>

<script>
    import mask from "@/util/mask"
    import { applicationState, fieldState, sessionState } from "@/state"
    import { indexBy } from "@/util/array"
    import { mapState } from "vuex"
    import Providers from "@/model/provider/Providers"
    import DynamicProviderKey from "@/model/provider/DynamicProviderKey"
    import HfdBankCardErrorModal from "@/components/provider/hfd/HfdBankCardErrorModal.vue"
    import { workflowService } from "@/service"
    import HFDSubmitBankCardAction from "@/action/actions/provider/HFDSubmitBankCardAction"
    import ProviderState from "@/state/ProviderState"
    import ProviderKey from "@/model/provider/ProviderKey"
    import PaymentMethodPurpose from "@/model/provider/PaymentMethodPurpose"

    export default {
        name: "HfdDownpaymentCollectionFormView",
        components: {
            HfdBankCardErrorModal,
        },
        data: () => ({
            fields: indexBy(fieldState.fields, "id"),
            autoShow: true,
            showCreditCardNumber: false,
            bankCardCardNumberMask: {
                mask: mask.hiddenCreditCardNumberMask,
                definitions: {
                    x: mask.star,
                },
            },
            zipCodeMask: mask.zipCode,
            years: [],
            cardApplication: {
                bankCardType: "CREDIT",
            },
            formSettings: {
                readOnly: false,
            },
            submitting: false,
            tab: null,
            bankCardTypes: ["Credit", "Debit", "HSA"],
            providerInfo: Providers[DynamicProviderKey.extractProviderKey(sessionState.providerKey)],
            showBankCardErrorModal: false,
            bankCardErrorMessage: "",
            providerKey: sessionState.providerKey,
            messages: {
                en: {
                    rule: {
                        validateExpirationMonthCheck: "Please check the month, it is no longer in this year",
                        validateExpiration: "Invalid Month",
                    },
                },
            },
            validations: {
                bankCardExpirationYear: {
                    validateExpirationMonthCheck: function (value, parentVm) {
                        const d = new Date()
                        let currentMonth = d.getMonth() + 1
                        let currentYear = d.getFullYear()
                        if (currentYear.toString() === value) {
                            return currentMonth <= parseInt(parentVm.bankCardExpirationMonth)
                        } else {
                            return true
                        }
                    },
                },
                bankCardExpirationMonth: {
                    validateExpiration(value, parentVm) {
                        const d = new Date()
                        let currentMonth = d.getMonth() + 1
                        let currentYear = d.getFullYear()
                        if (currentYear.toString() === parentVm.bankCardExpirationYear) {
                            return currentMonth <= value
                        } else {
                            return true
                        }
                    },
                },
            },
        }),

        computed: {
            ...mapState("application", {
                application: state => state.data,
            }),

            cardNumberValidations() {
                return {
                    invalid: value => this.validatePAN(value),
                }
            },
            zipCodeValidations() {
                return {
                    invalid: value => this.validateZipCode(value),
                }
            },

            downpaymentAmountDollars() {
                if (applicationState.selectedPaymentPlan) {
                    return applicationState.selectedPaymentPlan.downPaymentCents / 100
                }
                return 0
            },

            footerLogo() {
                const response = ProviderState.getPreAuthResponse()
                return Providers[ProviderKey.HFD_STANDARD]?.footerLogo[response.providerProductName]
            },

            backButtonDisabled() {
                return applicationState.resume?.signed !== true
            },

            downPaymentRequired() {
                if (applicationState.selectedPaymentPlan) {
                    return applicationState.selectedPaymentPlan.downPaymentCents !== 0
                }
                return false
            },

            optOutMessage() {
                const requiredModifier = this.downPaymentRequired ? "-downpayment-required" : ""
                return this.$t("down-payment.opt-out-checkbox" + requiredModifier, {
                    providerName: this.providerInfo.abbreviatedName,
                })
            },

            bankAccountPresent() {
                return this.application.bankAccount?.accountNumber
            },
            errorRetryMessage() {
                return this.bankCardErrorMessage
            },
        },

        methods: {
            focus() {
                if (this.autoShow) {
                    setTimeout(() => {
                        this.setCreditCardVisibility(true)
                    })
                }
            },

            backPath() {
                this.$router.back()
            },

            handleBankCardChange() {
                const d = new Date()
                let currentYear = d.getFullYear()
                if (currentYear.toString() === this.application.bankCard.expirationYear) {
                    setTimeout(() => {
                        this.$refs.bankCardExpirationYear.validate()
                    })
                }

                setTimeout(() => {
                    this.bankCardCardNumberMask = {
                        mask: mask.getBankCardMask(this.application.bankCard?.cardNumber, this.showCreditCardNumber),
                    }
                })
            },

            blur() {
                if (this.autoShow) {
                    setTimeout(() => {
                        this.setCreditCardVisibility(false)
                    })
                }
            },

            setCreditCardVisibility(flag) {
                this.showCreditCardNumber = flag
                this.bankCardCardNumberMask = {
                    mask: mask.getBankCardMask(this.application.bankCard?.cardNumber, this.showCreditCardNumber),
                }
            },

            toggleMask() {
                this.setCreditCardVisibility(!this.showCreditCardNumber)
            },

            async submit() {
                try {
                    this.submitting = true
                    const bankCard = applicationState.application.bankCard
                    const request = {
                        type: this.cardApplication.bankCardType,
                        cardNumber: bankCard?.cardNumber,
                        cardholderName: bankCard?.cardholderName,
                        expirationMonth: bankCard?.expirationMonth,
                        expirationYear: bankCard?.expirationYear,
                        zipCode: bankCard?.zipCode,
                        automaticPayments: false,
                        primary: false,
                        purpose: PaymentMethodPurpose.DOWNPAYMENT,
                    }

                    const app = applicationState.application
                    applicationState.application.bankCard.downPaymentMethod = this.cardApplication.bankCardType
                    applicationState.application.downPaymentBankCard = { ...app.bankCard }
                    const response = await workflowService.invoke(
                        new HFDSubmitBankCardAction(sessionState.providerKey, request, `loanPayment`)
                    )

                    if (this.providerInfo.showBankCardErrorModal && response?.errors?.length > 0) {
                        this.bankCardErrorMessage = response.errors[0].errorMessage
                        this.showBankCardErrorModal = true
                    }
                } finally {
                    this.submitting = false
                }
            },

            validatePAN(pan) {
                if (!pan?.length) {
                    return true
                }

                if (/[^0-9-\s]+/.test(pan)) {
                    return false
                }

                const association = pan.substring(0, 1)
                switch (association) {
                    case "3":
                        return this.validateAmex(pan)
                    case "4":
                        return this.validateVisa(pan)
                    case "5":
                        return this.validateMastercard(pan)
                    case "6":
                        return this.validateDiscover(pan)
                    default:
                        return false
                }
            },

            validateVisa(pan) {
                return pan?.length === 16 && this.luhn(pan)
            },

            validateAmex(pan) {
                return pan?.length === 15 && this.luhn(pan)
            },

            validateMastercard(pan) {
                return pan?.length === 16 && this.luhn(pan)
            },

            validateDiscover(pan) {
                return pan?.length === 16 && this.luhn(pan)
            },

            validateZipCode(zipcode) {
                return zipcode?.length === 5
            },

            closeBankCardErrorModal() {
                this.showBankCardErrorModal = false
            },

            luhn(pan) {
                let nCheck = 0
                let nDigit = 0
                let bEven = false

                for (let n = pan.length - 1; n >= 0; n--) {
                    let cDigit = pan.charAt(n)
                    nDigit = parseInt(cDigit, 10)

                    if (bEven) {
                        if ((nDigit *= 2) > 9) nDigit -= 9
                    }

                    nCheck += nDigit
                    bEven = !bEven
                }

                return nCheck % 10 === 0
            },
        },
    }
</script>
<style lang="scss" scoped>
    .submit-button {
        height: fit-content !important;
        min-width: 160px;
        white-space: normal;
        display: block;

        @media #{map-get($display-breakpoints, "sm-and-up")} {
            display: inline-flex;
            white-space: nowrap;
            width: auto;
            height: auto;
        }
    }

    h4 {
        font-weight: 600 !important;
        font-size: to_rem(22px) !important;
        line-height: to_rem(28px) !important;
        color: $primary-color;
    }
</style>
